import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

// ==================== ICONS ====================
const MicrophoneIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
  </svg>
);

const VideoCameraIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" d="M15.75 10.5l4.72-4.72a.75.75 0 011.28.53v11.38a.75.75 0 01-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 002.25-2.25v-9a2.25 2.25 0 00-2.25-2.25h-9A2.25 2.25 0 002.25 7.5v9a2.25 2.25 0 002.25 2.25z" />
  </svg>
);

const ChatBubbleIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-4.133a1.14 1.14 0 01.865-.501 48.172 48.172 0 003.423-.379c1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />
  </svg>
);

const SparklesIcon = ({ className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
  </svg>
);

const CheckCircleIcon = ({ className = "w-5 h-5" }) => (
  <svg className={className} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

// ==================== REUSABLE COMPONENTS ====================

const Container = ({ className = "", children }) => (
  <div className={`mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ${className}`}>{children}</div>
);

const SectionHeader = ({ eyebrow, title, description, align = "left" }) => (
  <div className={align === "center" ? "text-center max-w-3xl mx-auto" : "max-w-3xl"}>
    {eyebrow && (
      <div className={align === "center" ? "flex justify-center" : ""}>
        <span className="inline-flex items-center gap-2 text-school-goldDark font-semibold tracking-[0.18em] uppercase text-xs">
          <span className="h-1.5 w-1.5 rounded-full bg-school-gold" aria-hidden="true" />
          {eyebrow}
        </span>
      </div>
    )}
    <h2 className="mt-3 text-3xl sm:text-4xl md:text-5xl font-heading font-bold text-school-blue leading-[1.08] tracking-tight">
      {title}
    </h2>
    {description && (
      <p className={`mt-4 text-base md:text-lg text-gray-600 leading-7 ${align === "center" ? "mx-auto" : ""}`}>
        {description}
      </p>
    )}
  </div>
);

const MediaProjectCard = ({ image, title, subtitle, description }) => (
  <article className="group rounded-3xl border border-gray-100 bg-white overflow-hidden shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="aspect-[16/9] overflow-hidden bg-gray-100 relative">
      <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors z-10" />
      <img
        src={image}
        alt={title}
        loading="lazy"
        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
      />
    </div>
    <div className="p-7 md:p-8">
      <h3 className="text-xl md:text-2xl font-heading font-bold text-gray-900 mb-2 tracking-tight group-hover:text-school-gold transition-colors">
        {title}
      </h3>
      {subtitle && (
        <p className="text-sm font-semibold text-school-goldDark mb-3">
          {subtitle}
        </p>
      )}
      <p className="text-sm md:text-base text-gray-600 leading-7">
        {description}
      </p>
    </div>
  </article>
);

const SkillCard = ({ icon, title, description }) => (
  <div className="group flex flex-col p-8 bg-white rounded-3xl border border-gray-100 shadow-soft hover:shadow-md transition-all duration-300 hover:-translate-y-0.5">
    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-school-blue/8 text-school-blue group-hover:bg-school-blue group-hover:text-white transition-colors duration-300 mb-6 shadow-sm">
      {icon}
    </div>
    <h3 className="text-lg md:text-xl font-heading font-bold text-gray-900 mb-2 tracking-tight group-hover:text-school-gold transition-colors">
      {title}
    </h3>
    <p className="text-sm md:text-base text-gray-600 leading-7">{description}</p>
  </div>
);

// ==================== MAIN LTG PHASE 3 COMPONENT ====================

const LTGPhase3 = () => {
  const mediaProjects = [
    {
      title: 'Student Podcasts',
      image: 'https://3.files.edl.io/1e38/23/12/04/123359-096bb412-d91a-43fa-82d9-ff7a39e3bf0d.jpg',
      description: 'Students create and produce their own podcasts on various topics'
    },
    {
      title: 'Class 5-407 Productions',
      subtitle: 'Brought to you by Ms. Abreu and Ms. Moss\' Class',
      image: 'https://3.files.edl.io/7f82/24/05/06/015656-888cc181-8a5b-41fc-8318-d13a5250f4f8.jpg',
      description: 'Fifth grade students showcase their multimedia production skills'
    },
    {
      title: 'News Productions',
      image: 'https://3.files.edl.io/f6b3/24/05/06/152142-31c76d3a-9f9b-44af-ae4e-3ffb830265a6.jpg',
      description: 'Student-created news segments and broadcasts'
    },
    {
      title: 'Video Storytelling',
      image: 'https://3.files.edl.io/4bef/24/05/07/114425-16aaef8e-7b0d-4a52-b97b-3865581a3292.jpg',
      description: 'Creative video projects bringing student stories to life'
    }
  ];

  return (
    <div className="min-h-screen font-sans text-gray-600 antialiased selection:bg-school-gold/30 selection:text-school-blue" style={{ backgroundColor: '#F5EFE0' }}>
      <Navbar />

      <main id="main-content">

        {/* ==================== HERO SECTION ==================== */}
        <section className="relative min-h-[50vh] md:min-h-[60vh] flex items-center overflow-hidden bg-[#0F2240]">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(ellipse_at_top,rgba(255,255,255,0.35),transparent_55%)]" aria-hidden="true" />
          <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 70% 50% at 50% 100%, rgba(197,145,50,0.1) 0%, transparent 70%)' }} />

          <Container className="relative z-10 pt-32 pb-16 md:pt-40 md:pb-20">
            <div className="max-w-4xl bg-[#0F2240]">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-white/90 text-xs sm:text-sm font-semibold mb-7">
                <span className="w-2 h-2 rounded-full bg-orange-400" aria-hidden="true" />
                LTG Phase 3
              </div>

              <h1 className="text-4xl sm:text-5xl md:text-6xl font-heading font-bold text-white tracking-tight leading-[1.05]">
                LTG @ P.S. 85 Phase 3
              </h1>

              <p className="mt-6 text-lg md:text-xl text-gray-200 leading-relaxed max-w-3xl">
                Podcasts, Digital Media & Creative Storytelling
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== INTRODUCTION ==================== */}
        <section className="py-20 md:py-28 bg-[#F5EFE0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Phase 3"
              title="Advanced Digital Media Production"
              description="Empowering students to become confident content creators and digital communicators"
            />

            <div className="mt-10 max-w-4xl mx-auto">
              <p className="text-base md:text-lg text-gray-700 leading-relaxed text-center">
                In Phase 3, students explore advanced digital media production including podcasting, video creation, and multimedia storytelling. This phase empowers students to become confident content creators and digital communicators.
              </p>
            </div>
          </Container>
        </section>

        {/* ==================== MEDIA PROJECTS GRID ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Student Work"
              title="Featured Projects"
              description="Explore the creative work our students have produced"
            />

            <div className="mt-14 grid md:grid-cols-2 gap-8">
              {mediaProjects.map((project, index) => (
                <MediaProjectCard key={index} {...project} />
              ))}
            </div>
          </Container>
        </section>

        {/* ==================== SKILLS DEVELOPED ==================== */}
        <section className="py-20 md:py-28 bg-white">
          <Container>
            <SectionHeader
              align="center"
              eyebrow="Learning Outcomes"
              title="Skills Students Develop"
              description="Building essential 21st-century skills through digital media"
            />

            <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
              <SkillCard
                icon={<MicrophoneIcon />}
                title="Podcasting"
                description="Audio recording, editing, and narrative storytelling techniques."
              />

              <SkillCard
                icon={<VideoCameraIcon />}
                title="Video Production"
                description="Filming, editing, and producing professional video content."
              />

              <SkillCard
                icon={<ChatBubbleIcon />}
                title="Communication"
                description="Public speaking, interviewing, and presentation skills."
              />

              <SkillCard
                icon={<SparklesIcon />}
                title="Creativity"
                description="Original content creation and creative problem-solving."
              />
            </div>
          </Container>
        </section>

        {/* ==================== FEATURED CLASS HIGHLIGHT ==================== */}
        <section className="py-20 md:py-28 bg-[#EDE5D0]">
          <Container>
            <div className="rounded-3xl border border-school-gold/20 p-8 md:p-12 shadow-soft" style={{ backgroundColor: '#EDE5D0' }}>
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4 tracking-tight">
                  Student Showcase
                </h2>
                <p className="text-base md:text-lg text-gray-700 mb-8 leading-7">
                  Our students' podcasts, videos, and digital media projects demonstrate their creativity, technical skills, and commitment to quality storytelling.
                </p>
                <span className="inline-flex items-center gap-2 rounded-full bg-school-blue px-8 py-4 text-base font-bold text-white shadow-soft">
                  <CheckCircleIcon />
                  View Projects on School Portal
                </span>
              </div>
            </div>
          </Container>
        </section>

        {/* ==================== CTA SECTION ==================== */}
        <section className="relative py-24 md:py-32 bg-school-gold overflow-hidden">
          <div className="absolute -right-20 -top-20 w-96 h-96 bg-school-blue/8 rounded-full blur-3xl pointer-events-none" />

          <Container className="relative text-center">
            <h2 className="text-4xl md:text-6xl font-heading font-bold text-school-blue mb-6">
              Explore All LTG Phases
            </h2>
            <p className="text-xl text-school-blue max-w-2xl mx-auto mb-10 font-light">
              See how our Learning Technology Grant program has evolved through each phase.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/ltg-phase-1"
                className="px-8 py-4 rounded-full bg-school-blue text-white font-bold hover:bg-[#0F2240] transition-colors shadow-soft"
              >
                View Phase 1
              </a>
              <a
                href="/nysed-grant-2025"
                className="px-8 py-4 rounded-full bg-white/30 text-school-blue border-2 border-school-blue/20 font-medium hover:bg-white/50 transition-colors"
              >
                Back to Grant Overview
              </a>
            </div>
          </Container>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default LTGPhase3;
